import { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import {
  KeyIcon,
  ShieldCheckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  useAdminUserDetail,
  useAdminRolesList,
  useAdminOrganizations,
  useImpersonateUser,
  useSetUserActivation,
  type AdminUser,
} from '../../api/extensions/useAdminOps';
import { useOrganizationsList } from '../../api/generated_hooks/generated-organizations-hooks';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { authService } from '../../services/auth.service';
import { resetClientState } from '../../lib/resetClientState';
import { notify } from '../../utils/notifications';
import AdminResetPasswordModal from '../../components/AdminResetPasswordModal';
import DeactivateUserDialog from '../../components/DeactivateUserDialog';
import UserManagementModal from '../../components/UserManagementModal';
import FinancialsTab from './user-details/FinancialsTab';
import MembershipsTab from './user-details/MembershipsTab';
import EntriesTab from './user-details/EntriesTab';
import ProfileTab from './user-details/ProfileTab';

const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';

// `roles` was a dedicated tab but its functionality (memberships + the
// super-admin toggle) now lives entirely inside the Org Memberships tab.
// Deep links carrying `?tab=roles` fall through to `profile` via
// `resolveTab` since the value is no longer in this list.
const VALID_TABS = ['profile', 'memberships', 'entries', 'financials'] as const;
type TabId = (typeof VALID_TABS)[number];

function resolveTab(value: string | null): TabId {
  return (VALID_TABS as readonly string[]).includes(value ?? '')
    ? (value as TabId)
    : 'profile';
}

function getDisplayName(user: AdminUser): string {
  const display = [user.display_first_name, user.display_last_name].filter(Boolean).join(' ');
  if (display) return display;
  return (
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.primary_email ||
    'Unknown user'
  );
}

export default function UserDetailsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = resolveTab(searchParams.get('tab'));

  const isAllOrgAdmin = useAuthStore((s) => s.isAllOrgAdmin());
  const currentUserProfileId = useAuthStore((s) => s.profile?.id);
  const setAuthData = useAuthStore((s) => s.setAuthData);

  const { data, isLoading, error } = useAdminUserDetail(userId);
  const user = data?.user;

  // Modal state. Profile edits are now done inline on the Profile tab
  // (see `ProfileTab`) — the old EditUserModal lives on for the Manage Users
  // list view but is no longer reachable from this page.
  const [managingRoles, setManagingRoles] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  // Data needed by the inline Memberships + Roles tabs — load up-front so switching
  // doesn't flash an empty role/org dropdown.
  const { data: rolesData } = useAdminRolesList();
  const { data: adminOrgsData } = useAdminOrganizations();
  const { data: allOrgsData } = useOrganizationsList();

  const impersonateUser = useImpersonateUser();
  const setActivation = useSetUserActivation();

  // Selectable orgs mirror ManageUsersPage: ALL-org admins see every real
  // org; org-scoped admins are limited to the orgs they administer.
  const selectableOrgs = useMemo(() => {
    if (isAllOrgAdmin && Array.isArray(allOrgsData)) {
      return (allOrgsData as any[])
        .filter((o) => o.id !== ALL_ORG_ID)
        .map((o) => ({
          id: o.id as string,
          name: o.name as string,
          short_name: o.short_name as string | undefined,
        }));
    }
    return (adminOrgsData?.orgs ?? []).filter((o) => o.id !== ALL_ORG_ID);
  }, [isAllOrgAdmin, allOrgsData, adminOrgsData]);

  const managedOrgIds = useMemo(() => {
    if (isAllOrgAdmin) return undefined;
    return (adminOrgsData?.orgs ?? [])
      .map((o) => o.id)
      .filter((id) => id !== ALL_ORG_ID);
  }, [isAllOrgAdmin, adminOrgsData]);

  const handleTabChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'profile') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  /**
   * Begin impersonating this user. Mirrors the ManageUsersPage flow: swap
   * the session_id cookie via the backend, wipe React Query cache so admin-
   * scoped data doesn't leak through, route to /dashboard BEFORE flipping
   * the auth store (admin-only route guard would otherwise win the race and
   * bounce to /unauthorized), then refresh the session under the new identity.
   */
  // Soft-deactivation toggle. Deactivation routes through
  // DeactivateUserDialog so the admin can attach an optional reason
  // that's shown to the user on the landing page; reactivation is a
  // single click — no extra info to collect.
  const handleActivationButtonClick = () => {
    if (!user) return;
    const target = !(user.is_active ?? true);
    if (target) {
      // Reactivate path — fire and forget.
      setActivation.mutate(
        { userId: user.id, isActive: true },
        {
          onSuccess: () => notify.success(`${displayName} reactivated.`),
          onError: (err: any) =>
            notify.error(err?.message || 'Failed to update account status'),
        },
      );
      return;
    }
    setDeactivating(true);
  };

  const handleDeactivateConfirm = async (reason: string | null) => {
    if (!user) return;
    try {
      await setActivation.mutateAsync({
        userId: user.id,
        isActive: false,
        reason,
      });
      notify.success(`${displayName} deactivated.`);
      setDeactivating(false);
    } catch (err: any) {
      notify.error(err?.message || 'Failed to update account status');
    }
  };

  const handleImpersonate = async () => {
    if (!user) return;
    try {
      const result = await impersonateUser.mutateAsync(user.id);
      resetClientState(queryClient);
      navigate('/dashboard', { replace: true });
      const session = await authService.validateSession();
      setAuthData({
        user: session.user,
        profile: session.profile,
        role: session.role,
        permissions: session.permissions,
        impersonator: session.impersonator ?? null,
      });
      notify.success(`Now impersonating ${result.targetName}`);
    } catch (err: any) {
      notify.error(err?.message || 'Failed to impersonate user');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-4 max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/users')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to users
        </Button>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
          <h3 className="font-medium text-destructive">Could not load user</h3>
          <p className="text-sm text-destructive/80 mt-1">
            {(error as Error)?.message || 'User not found.'}
          </p>
        </div>
      </div>
    );
  }

  const isSelf = !!currentUserProfileId && currentUserProfileId === user.id;
  const displayName = getDisplayName(user);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/users')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to users
          </Button>
        </div>

        <header className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold">{displayName}</h1>
                {user.is_active === false && (
                  <Badge variant="destructive" aria-label="Account deactivated">
                    Deactivated
                  </Badge>
                )}
                {/* Quick-action icons sit next to the name so admins can
                    reset a password, open the roles modal, or impersonate
                    without scrolling through tabs. The modal-based roles
                    form (icon below) is the same one used from the Manage
                    Users list view — kept here for parity with that flow. */}
                <div className="flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setManagingRoles(true)}
                        className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                        aria-label="Manage roles and organizations"
                      >
                        <ShieldCheckIcon className="h-5 w-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Manage roles &amp; organizations</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setResettingPassword(true)}
                        className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
                        aria-label="Reset password or confirm email"
                      >
                        <KeyIcon className="h-5 w-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Reset password / confirm email</TooltipContent>
                  </Tooltip>
                  {!isSelf && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleImpersonate}
                          disabled={impersonateUser.isPending}
                          className="p-1.5 rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-accent disabled:opacity-50"
                          aria-label={`Impersonate ${displayName}`}
                        >
                          {impersonateUser.isPending ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <UserCircleIcon className="h-5 w-5" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Impersonate user (sign in as them to debug what they see)
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {user.primary_email ?? 'No email on file'}
                {user.phone ? ` • ${user.phone}` : ''}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex flex-wrap gap-1.5 justify-end">
                {user.organization_memberships.map((m) => (
                  <Badge key={m.organization_id} variant={m.is_active ? 'default' : 'secondary'}>
                    {m.organization.short_name || m.organization.name}
                  </Badge>
                ))}
              </div>
              {/* Activation toggle. Hidden when looking at your own account
                  because the backend refuses self-deactivation — surfacing
                  the button there would just produce an error. */}
              {!isSelf && (
                <Button
                  type="button"
                  size="sm"
                  variant={user.is_active === false ? 'default' : 'outline'}
                  onClick={handleActivationButtonClick}
                  disabled={setActivation.isPending}
                  aria-label={user.is_active === false ? 'Reactivate account' : 'Deactivate account'}
                >
                  {setActivation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {user.is_active === false ? 'Reactivate Account' : 'Deactivate Account'}
                </Button>
              )}
            </div>
          </div>
        </header>

        <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-4 sm:w-auto sm:inline-flex">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="memberships">Org Memberships</TabsTrigger>
            <TabsTrigger value="entries">Event History</TabsTrigger>
            <TabsTrigger value="financials">Financials</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4">
            <ProfileTab user={user} />
          </TabsContent>

          <TabsContent value="memberships" className="mt-4">
            <MembershipsTab
              user={user}
              isAllOrgAdmin={isAllOrgAdmin}
              managedOrgIds={managedOrgIds}
              availableRoles={rolesData?.roles ?? []}
              selectableOrgs={selectableOrgs as any}
              currentUserProfileId={currentUserProfileId}
            />
          </TabsContent>

          <TabsContent value="entries" className="mt-4">
            <EntriesTab user={user} />
          </TabsContent>

          <TabsContent value="financials" className="mt-4">
            <FinancialsTab user={user} isAllOrgAdmin={isAllOrgAdmin} />
          </TabsContent>
        </Tabs>

        {/* Roles modal — same component used on the Manage Users list view.
            Reachable from the shield-icon button above; the per-org role
            edits also live inline on the Org Memberships tab. */}
        <UserManagementModal
          isOpen={managingRoles}
          onClose={() => setManagingRoles(false)}
          user={user}
          availableRoles={rolesData?.roles ?? []}
          availableOrgs={selectableOrgs as any}
          isSystemAdmin={isAllOrgAdmin}
          managedOrgIds={managedOrgIds}
          currentUserProfileId={currentUserProfileId}
        />

        {/* Admin password reset (also confirms email + revokes sessions) */}
        <AdminResetPasswordModal
          isOpen={resettingPassword}
          onClose={() => setResettingPassword(false)}
          userId={user.id}
          userName={displayName}
          userEmail={user.primary_email ?? null}
        />

        {/* Soft-deactivation dialog — captures an optional reason that the
            user sees on /account-deactivated when they next try to log in. */}
        <DeactivateUserDialog
          isOpen={deactivating}
          onClose={() => setDeactivating(false)}
          onConfirm={handleDeactivateConfirm}
          isSubmitting={setActivation.isPending}
          userName={displayName}
        />
      </div>
    </TooltipProvider>
  );
}

